import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';

import { gql } from 'apollo-angular';

import { AbstractDocumentsService, QueryManagerService } from 'finzzle-angular-utils';
import {} from 'rxjs';
import { CreateStorageFileInput, FileMetadata, Maybe, StorageFile } from '../models/generated/graphql';
import {
  JPEG_EXTENSIONS,
  JPEG_MIME_TYPE,
  PDF_EXTENSIONS,
  PDF_MIME_TYPE,
  PNG_EXTENSIONS,
  PNG_MIME_TYPE,
} from '../models/graphqlData';

export type ZipBlobItem = {
  name: string;
  lastModified: Date;
  input: Blob;
};

const SIZE_LIMIT = 19 * 1024 * 1024; // 20MB

export class FicheStorageFile extends File {
  storageFile?: StorageFile;
}

export const PDF_BY_ID = gql`
  mutation pdfById($id: String!) {
    fichePDF(ficheId: $id) {
      id
      fileName
      # consultantId
      fileExtension
      denomination
      createdDate
      lastModificationDate
      fileConnectionInfo {
        sasUrl
        expiration
        permissions
        blobFileId
      }
      fileNameWithExtension
    }
  }
`;

const GET_FILECONNECTIONINFO_BY_TEMPLATENAME = gql`
  query templateFileByName($templateName: String!) {
    templateFileByName(templateName: $templateName) {
      id
      fileName
      fileExtension
      fileType
      fileConnectionInfo {
        sasUrl
        expiration
      }
    }
  }
`; // templateFileByName

const GET_FILECONNECTIONINFO_BY_ID = gql`
  query storageFile($storageFileId: String!) {
    storageFile(storageFileId: $storageFileId) {
      id
      fileName
      fileExtension
      fileType
      fileConnectionInfo {
        sasUrl
        expiration
      }
    }
  }
`;

const CREATE_EMPTY_GED_FILE_MUTATION = gql`
  mutation createEmptyGedFile($fileName: String!, $typeCode: String!, $denomination: String!, $investisseurId: Long!) {
    createEmptyGedFile(
      denomination: $denomination
      nomDeFichier: $fileName
      investisseurId: $investisseurId
      typeCode: $typeCode
    ) {
      fileId
      versionNumber
      url
      investisseurId
      extension
      denomination
      typeDocumentCode
      dateDeCreation
      creePar
      dateDeDerniereModification
      derniereModificationPar
      fileName
      dateSignature
      urlExpireDate
      permission
      typeLibelle
    }
  }
`;

const UPDATE_GED_FILE_METADATA_MUTATION = gql`
  mutation updateGedFileMetadata($fileId: String!, $metadata: FileMetadataUpdateInput!) {
    updateGedFileMetadata(fileId: $fileId, newValue: $metadata) {
      fileId
      denomination
      typeLibelle
      typeDocumentCode
      dateDeDerniereModification
    }
  }
`;

const GET_FILE_METADATA_BY_ID = gql`
  query gedFileMetadataById($id: String!) {
    gedFileMetadataById(id: $id) {
      fileId
      versionNumber
      url
      extension
      actif
      urlExpireDate
      permission
    }
  }
`;


const CREATE_FICHE_STORAGE_FILE = gql`
  mutation createFicheStorageFile($input: CreateStorageFileInput!) {
    createFicheConseilStorageFile(input: $input) {
      id
      denomination
      fileName
      fileType
      fileNameWithExtension
      fileConnectionInfo {
        sasUrl
        permissions
      }
    }
  }
`;

const CREATE_TEMPORARY_STORAGE_FILE = gql`
  mutation createTemporaryStorageFile($input: CreateStorageFileInput!) {
    createTemporaryStorageFile(input: $input) {
      id
      denomination
      fileNameWithExtension
      fileConnectionInfo {
        sasUrl
        permissions
      }
    }
  }
`;

const UPLOAD_TEMPLATE_FILE = gql`
  mutation uploadTemplateFile($templateUrl: String!, $templateName: String!) {
    uploadTemplateFile(templateUrl: $templateUrl, templateName: $templateName)
  }
`;

@Injectable({
  providedIn: 'root',
})
export class DocumentsService extends AbstractDocumentsService {
  override async createEmptyFile(
    fileName: string,
    ownerId: number,
    denomination: string | undefined
  ): Promise<{ url: string; fileId: string } | null | undefined> {
    var result = await firstValueFrom(
      this.queryManager.mutate<{ createEmptyGedFile: FileMetadata }>({
        mutation: CREATE_EMPTY_GED_FILE_MUTATION,
        variables: {
          fileName: fileName,
          denomination: denomination,
          investisseurId: ownerId,
        },
      })
    );
    if (result.data?.createEmptyGedFile?.fileId) {
      return { fileId: result.data?.createEmptyGedFile?.fileId ?? '', url: result.data?.createEmptyGedFile?.url ?? '' };
    }
    return null;
  }


  constructor(
    http: HttpClient,
    domSanitizer: DomSanitizer,
    private queryManager: QueryManagerService,
    @Inject('environment') environment: any
  ) {
    super(http, domSanitizer, environment);
  }

  async getStorageFile(fileId: string): Promise<StorageFile | null> {
    const result = await firstValueFrom(
      this.queryManager.mutate<{ storageFile: StorageFile }>({
        mutation: GET_FILECONNECTIONINFO_BY_ID,
        variables: { storageFileId: fileId },
        fetchPolicy: 'network-only',
      })
    );

    return result.data?.storageFile ?? null;
  }

  async getTemplateFile(templateName: string): Promise<StorageFile | null> {
    const result = await firstValueFrom(
      this.queryManager.query<{ templateFileByName: StorageFile }>({
        query: GET_FILECONNECTIONINFO_BY_TEMPLATENAME,
        variables: { templateName: templateName },
        fetchPolicy: 'network-only',
      })
    );

    return result.data?.templateFileByName ?? null;
  }

  async fetchAndCacheStorageDocument(storage: StorageFile): Promise<string | null> {
    // if storage does not contains url or extension, do fetch storage for API
    let workingFile = storage;
    if (!storage.fileConnectionInfo?.sasUrl && storage.id) {
      const storageInfo = await this.getStorageFile(storage.id); //TODO : creer route
      if (storageInfo) {
        workingFile = storageInfo;
      } else {
        // fileMetadata does not contains url or extension and fetch from APi return nothing : return null
        return null;
      }
    } else if (!(storage.fileConnectionInfo?.sasUrl && storage.id)) {
      // fileMetadata does not contains url or extension and no fileId available to fetch date : return null
      return null;
    }

    let sanitizedDocumentUrl = this.domSanitizer.sanitize(
      SecurityContext.URL,
      workingFile.fileConnectionInfo?.sasUrl ?? null
    );

    if (sanitizedDocumentUrl) {
      try {
        const result = await firstValueFrom(this.http.get(sanitizedDocumentUrl, { responseType: 'blob' }));
        let typedBlob = new Blob([result], {
          type: workingFile.fileType ?? this.getMimeType(workingFile.fileExtension),
        });
        const file = new File([typedBlob], workingFile.fileName ?? workingFile.denomination ?? 'unknown', {
          type: typedBlob.type,
        });
        return URL.createObjectURL(file);
      } catch (error) {
        // if there is an error, try to fetch new url from API and retry
        if (workingFile.id) {
          const updatedFile = await this.getStorageFile(workingFile.id);
          if (updatedFile) {
            return this.fetchAndCacheBlobDocument(updatedFile.fileConnectionInfo?.sasUrl, updatedFile.fileExtension);
          }
        }
        return null;
      }
    }
    return null;
  }

  getMimeType(extension: Maybe<string> | undefined): string {
    if (extension) {
      if (PDF_EXTENSIONS.includes(extension.toLowerCase())) {
        return PDF_MIME_TYPE;
      }
      if (JPEG_EXTENSIONS.includes(extension.toLowerCase())) {
        return JPEG_MIME_TYPE;
      }
      if (PNG_EXTENSIONS.includes(extension.toLowerCase())) {
        return PNG_MIME_TYPE;
      }
    }
    return PDF_MIME_TYPE;
  }

  isAuthorizedFileSize(file: File): boolean {
    return file.size < SIZE_LIMIT;
  }
  isAuthorizedFileType(file: File): boolean {
    return file.type === PDF_MIME_TYPE || file.type === JPEG_MIME_TYPE || file.type === PNG_MIME_TYPE;
  }




  async createFicheConseilStorageFile(file: CreateStorageFileInput): Promise<StorageFile | null> {
    let result = await firstValueFrom(
      this.queryManager.mutate<{ createFicheConseilStorageFile: StorageFile }>({
        mutation: CREATE_FICHE_STORAGE_FILE,
        variables: { input: file },
      })
    );

    return result.data?.createFicheConseilStorageFile ?? null;
  }

  async createTemporaryStorageFile(file: CreateStorageFileInput): Promise<StorageFile | null> {
    let result = await firstValueFrom(
      this.queryManager.mutate<{ createTemporaryStorageFile: StorageFile }>({
        mutation: CREATE_TEMPORARY_STORAGE_FILE,
        variables: { input: file },
      })
    );

    return result.data?.createTemporaryStorageFile ?? null;
  }

  async uploadTemplateFile(templateUrl: string, templateName: string): Promise<string | null> {
    let result = await firstValueFrom(
      this.queryManager.mutate<{ uploadTemplateFile: string }>({
        mutation: UPLOAD_TEMPLATE_FILE,
        variables: { templateUrl: templateUrl, templateName: templateName },
      })
    );

    return result.data?.uploadTemplateFile ?? null;
  }
  async uploadFicheStorageFiles(files: FicheStorageFile[], isTemporary = false): Promise<FicheStorageFile[]> {
    const storageFilesPromises = files.map(async file => {
      // If file is not in storage, create it and upload it
      if (!file.storageFile) {
        const input: CreateStorageFileInput = {
          fileName: file.name,
          fileType: file.type,
        };
        const storageFile = isTemporary
          ? await this.createTemporaryStorageFile(input)
          : await this.createFicheConseilStorageFile(input);

        file.storageFile = storageFile ?? undefined;

        if (file.storageFile?.fileConnectionInfo?.sasUrl) {
          await this.uploadBlobFileContent(file, file.storageFile.fileConnectionInfo.sasUrl);
        }
      }

      return file;
    });

    const result = await Promise.all(storageFilesPromises);
    return result;
  }

  async displayStorageFileInNewTab(storageFile: StorageFile): Promise<void> {
    const url = await this.fetchAndCacheStorageDocument(storageFile);
    if (url) {
      window.open(url, '_blank');
    }
  }
}
